/**
 * Pure (DOM-free) rules + vocab for the Research Intake form (E06-F01-T05/T06/T07).
 * Kept out of the React components so the decisions are unit-testable and shared
 * between the form and its toggle sub-components.
 *
 *   - `emptyIntakeCriteria` — a fully-defaulted IntakeCriteria (matches the shared
 *     schema defaults: addClientToDns=true, gradeScope='A+B', verifyStrictness=
 *     'strict', purpose='sourcing', targetCount=100, all collections []);
 *   - `purposeHint` — the dynamic #purpose-hint copy (T06);
 *   - `gradeCardVisible` — which grade cards (B / C) show for a given scope (T06);
 *   - the company-type / funding preset vocab (T07, incl. non-IT donors);
 *   - `purposeOptions` / `gradeScopeOptions` / `verifyOptions` — the toggle option
 *     lists (machine value + human label).
 */
import {
  INTAKE_RESEARCH_PURPOSES,
  GRADE_SCOPES,
  VERIFY_STRICTNESS,
  TARGET_COUNT_DEFAULT,
  TARGET_COUNT_MIN,
  TARGET_COUNT_MAX,
  type IntakeCriteria,
  type IntakeLocation,
  type IntakeResearchPurpose,
  type GradeScope,
  type VerifyStrictness,
  type ResearchCriteria,
  type CriteriaNormalizeResult,
  type SimilarListsRequest,
} from '@eer/shared';

/** A fully-defaulted intake criteria object (round-trips through the schema). */
export function emptyIntakeCriteria(): IntakeCriteria {
  return {
    // null = no existing client resolved yet → the API create step mints a new
    // client from `clientName` (see intakeCriteriaSchema.clientId docs).
    clientId: null,
    clientName: '',
    clientWebsite: '',
    clientAbout: '',
    addClientToDns: true,
    industry: '',
    subCategories: [],
    locations: [],
    foundedFrom: null,
    foundedTo: null,
    // Units are always kilometers now (the mi/km toggle was removed) — the field
    // is kept only to satisfy the shared schema default; it is no longer shown.
    metroUnit: 'km',
    locationExclusions: [],
    sizeGlobal: '',
    sizeLocalCap: null,
    companyTypes: [],
    fundingStages: [],
    competitors: [],
    dnsCompanies: [],
    dnsIndustries: [],
    avoid: [],
    // E07-F07-T03 — «Exclude EER clients (global list)» is ON by default.
    useGlobalDns: true,
    purpose: 'sourcing',
    gradeScope: 'A+B',
    gradeDefinitions: { a: '', b: '', c: '' },
    verifyStrictness: 'strict',
    freeText: '',
    linkedinUrl: '',
    targetCount: TARGET_COUNT_DEFAULT,
    softCriteria: [],
  };
}

// ---------------------------------------------------------------------------
// Purpose (T06) — dynamic hint
// ---------------------------------------------------------------------------

/** The #purpose-hint copy that swaps with the Purpose toggle (T06 acceptance). */
export function purposeHint(purpose: IntakeResearchPurpose): string {
  return purpose === 'market_mapping'
    ? 'Market Mapping — exhaustively find ALL companies in the market (higher target count, wider discovery fan-out).'
    : 'Sourcing — a ranked long-list, relevance-first.';
}

/** Purpose toggle options (machine value + label). */
export const PURPOSE_OPTIONS: readonly { value: IntakeResearchPurpose; label: string }[] = [
  { value: 'sourcing', label: 'Sourcing' },
  { value: 'market_mapping', label: 'Market Mapping' },
];

// ---------------------------------------------------------------------------
// Grade scope (T06) — card visibility (applyGradeScope)
// ---------------------------------------------------------------------------

/**
 * Which optional grade cards are visible for a scope (applyGradeScope):
 *   'A'      → only A (B and C hidden);
 *   'A+B'    → A and B (C hidden);
 *   'A+B+C'  → A, B and C.
 * The A card is always visible.
 */
export function gradeCardVisible(scope: GradeScope, grade: 'b' | 'c'): boolean {
  if (grade === 'b') return scope !== 'A';
  return scope === 'A+B+C'; // grade C
}

/** Grade-scope toggle options. */
export const GRADE_SCOPE_OPTIONS: readonly { value: GradeScope; label: string }[] =
  GRADE_SCOPES.map((s) => ({ value: s, label: s }));

// ---------------------------------------------------------------------------
// Verify strictness (T07)
// ---------------------------------------------------------------------------

/** The verify-strictness explanation copy (T07). */
export function verifyHint(strictness: VerifyStrictness): string {
  return strictness === 'strict'
    ? 'Strict — PASS requires an explicit relevance confirmation. "used in high-tech" / "may work with" ≠ PASS (UCT rule).'
    : 'Lenient — "may work with" is accepted as PASS.';
}

/** Verify-strictness toggle options. */
export const VERIFY_OPTIONS: readonly { value: VerifyStrictness; label: string }[] = [
  { value: 'strict', label: 'Strict' },
  { value: 'lenient', label: 'Lenient' },
];

// ---------------------------------------------------------------------------
// Company-type / funding presets (T07) — incl. non-IT donors
// ---------------------------------------------------------------------------

/**
 * Company-type preset pills. Expanded beyond IT/startups to cover non-IT donors
 * (Manufacturer/Plant, Enterprise/Corporation, Distributor, …) per T07.
 */
export const COMPANY_TYPE_PRESETS = [
  'Product/SaaS',
  'Manufacturer/Plant',
  'Enterprise/Corporation',
  'Services',
  'Vendor/OEM',
  'Distributor',
] as const;

/** Funding-stage preset pills (incl. VC-funded / Public / Unicorn). */
export const FUNDING_PRESETS = [
  'VC-funded',
  'Public',
  'Unicorn',
  'Bootstrapped',
  'PE-owned',
] as const;

// ---------------------------------------------------------------------------
// Company-size bands (T05) — the prototype SELECT (Any / 1–50 / … / 1000+)
// ---------------------------------------------------------------------------

/**
 * The global-size band SELECT options (verbatim from the prototype intake:
 * `Any / 1–50 / 50–500 / 500–1000 / 1000+`, research.proto.js L407-411). The
 * empty string is the "Any" sentinel — it round-trips as `sizeGlobal: ''`
 * (nothing is persisted for "Any", matching emptyIntakeCriteria).
 */
export const COMPANY_SIZE_BANDS = [
  { value: '', label: 'Any' },
  { value: '1–50', label: '1–50' },
  { value: '50–500', label: '50–500' },
  { value: '500–1000', label: '500–1000' },
  { value: '1000+', label: '1000+' },
  // E07-F02-T19 — an upper bound as briefs write it ("≤300 globally").
  { value: '≤300', label: '≤300' },
] as const;

/** The canonical band values (for validation / the clone mapper's snap). */
export const COMPANY_SIZE_BAND_VALUES: readonly string[] = COMPANY_SIZE_BANDS.map((b) => b.value);

/** A trailing headcount unit or scope word ("employees", "globally", "по миру"). */
const SIZE_TAIL =
  /\s*(employees?|people|staff|сотрудник\p{L}*|globally|global|worldwide|world-wide|group-wide|in\s+total|total|по\s+миру|глобально|всего)\s*$/u;

/**
 * Normalise a typed / pasted / cloned size string into the value the size field
 * keeps. The field is the GLOBAL (whole-company) headcount, so a scope tail
 * ("globally", "worldwide", "по миру") is dropped. E07-F02-T19 — any window is kept,
 * not only the presets (the old SELECT snapped "≤300" to Any):
 *   - "≤300" / "<=300" / "up to 300" / "до 300" / "max 300" → "≤300";
 *   - "11-500" / "11 – 500" / "11 to 500" → "11–500";
 *   - "1000+" / "1000" (the legacy one-number signature) → "1000+";
 * anything unrecognised falls back to '' (Any).
 */
export function snapSizeBand(raw: string | null | undefined): string {
  let value = (raw ?? '')
    .trim()
    .toLowerCase()
    .replace(/(\d)[,\s](?=\d{3}\b)/g, '$1');
  // "300 employees globally" — up to two tails (unit + scope), in either order.
  for (let i = 0; i < 2; i += 1) value = value.replace(SIZE_TAIL, '').trim();
  if (!value) return '';
  if (COMPANY_SIZE_BAND_VALUES.includes(value)) return value;
  const upper = value.match(
    /^(<=|=<|≤|<|up\s+to|max(?:imum)?\.?|no\s+more\s+than|at\s+most|до|не\s+более|не\s+больше|максимум)\s*(\d+)$/u,
  );
  if (upper) {
    const n = Number(upper[2]);
    return `≤${upper[1] === '<' ? Math.max(0, n - 1) : n}`;
  }
  const range = value.match(/^(\d+)\s*(?:-|–|—|to|до)\s*(\d+)$/u);
  if (range) return `${Number(range[1])}–${Number(range[2])}`;
  const plus = value.match(/^(?:(?:>=|≥|from|от|at\s+least)\s*)?(\d+)\s*(?:\+|plus)?$/u);
  if (plus) return `${Number(plus[1])}+`;
  return '';
}

// ---------------------------------------------------------------------------
// Structured size-window input (E07-F02-T19 rework) — the form no longer takes
// free text: a mode SELECT + number field(s). The stored value stays the same
// string contract the API parses ("≤300" / "11–500" / "1000+" / '' = any).
// ---------------------------------------------------------------------------

/** How the global-size window is bounded. */
export type SizeWindowMode = 'any' | 'max' | 'range' | 'min';

/** The mode SELECT options — one kind of bound each, so no preset overlaps another. */
export const SIZE_WINDOW_MODES: ReadonlyArray<{ value: SizeWindowMode; label: string }> = [
  { value: 'any', label: 'Any size' },
  { value: 'max', label: 'Up to N employees (≤ N)' },
  { value: 'range', label: 'Between N and M employees' },
  { value: 'min', label: 'N employees or more (N+)' },
];

/** The decomposed window the structured input edits. */
export interface SizeWindowParts {
  mode: SizeWindowMode;
  min: number | null;
  max: number | null;
}

/** Decompose a stored size value into mode + bounds (unrecognised → any). */
export function sizeWindowParts(value: string | null | undefined): SizeWindowParts {
  const v = snapSizeBand(value);
  let m = v.match(/^≤(\d+)$/u);
  if (m) return { mode: 'max', min: null, max: Number(m[1]) };
  m = v.match(/^(\d+)–(\d+)$/u);
  if (m) return { mode: 'range', min: Number(m[1]), max: Number(m[2]) };
  m = v.match(/^(\d+)\+$/u);
  if (m) return { mode: 'min', min: Number(m[1]), max: null };
  return { mode: 'any', min: null, max: null };
}

const posInt = (n: number | null | undefined): n is number =>
  typeof n === 'number' && Number.isFinite(n) && n >= 0;

/**
 * Compose the stored value from the structured input. An incomplete or invalid
 * window (missing number, min > max) yields '' — i.e. no size filter — and
 * {@link sizeWindowError} says why.
 */
export function formatSizeWindow(parts: SizeWindowParts): string {
  const min = posInt(parts.min) ? Math.floor(parts.min) : null;
  const max = posInt(parts.max) ? Math.floor(parts.max) : null;
  switch (parts.mode) {
    case 'max':
      return max != null && max > 0 ? `≤${max}` : '';
    case 'min':
      return min != null ? `${min}+` : '';
    case 'range':
      return min != null && max != null && min <= max ? `${min}–${max}` : '';
    default:
      return '';
  }
}

/** A validation message for an incomplete window, or null when it is fine. */
export function sizeWindowError(parts: SizeWindowParts): string | null {
  if (parts.mode === 'any') return null;
  if (formatSizeWindow(parts) !== '') return null;
  if (parts.mode === 'range' && posInt(parts.min) && posInt(parts.max))
    return '"From" must not be greater than "to" — no size filter applied.';
  return 'Enter the number of employees — until then no size filter is applied.';
}

/**
 * Plain-language meaning of a stored window, shown under the field so the user
 * sees exactly which companies pass ("≤300" vs "11–500" are different filters,
 * not overlapping presets).
 */
export function describeSizeWindow(value: string | null | undefined): string {
  const p = sizeWindowParts(value);
  switch (p.mode) {
    case 'max':
      return `Keeps companies with at most ${p.max} employees worldwide (whole group).`;
    case 'range':
      return `Keeps companies with ${p.min} to ${p.max} employees worldwide (whole group).`;
    case 'min':
      return `Keeps companies with ${p.min} or more employees worldwide (whole group).`;
    default:
      return 'No size filter — companies of any size are kept.';
  }
}

// ---------------------------------------------------------------------------
// Cost / ETA footer copy (T05) — the prototype estimate strip
// ---------------------------------------------------------------------------

/**
 * The cost/ETA footer copy (verbatim from the prototype intake footer,
 * research.proto.js L522-524). Kept here so the exact caption is unit-pinned.
 */
export const INTAKE_COST_ESTIMATE = {
  cold: '$2.5–3.5',
  warm: '$0.3–0.8',
  eta: '~3–6 min',
} as const;

// ---------------------------------------------------------------------------
// paste-to-parse accelerator (T05) — client-side heuristic autofill
// ---------------------------------------------------------------------------

/** One parsed "Label: value" line from a pasted recruiter brief. */
type ParsedField = Partial<{
  clientName: string;
  clientWebsite: string;
  industry: string;
  subCategories: string[];
  locations: string[];
  sizeGlobal: string;
  companyTypes: string[];
  gradeA: string;
  gradeB: string;
  dnsCompanies: string[];
  avoid: string[];
  freeText: string;
}>;

/** Split a comma / semicolon / slash list into trimmed non-empty tokens. */
function splitList(value: string): string[] {
  return value
    .split(/[,;/]/)
    .map((s) => s.trim())
    .filter(Boolean);
}

/**
 * E12-F17-T12 — normalized labels of a "companies to avoid" line. Those companies
 * are Do-Not-Source (`dnsCompanies`), never Competitors (the Grade-A seeds).
 */
const COMPANY_AVOID_LABELS = [
  'competitorstoavoid',
  'companiestoavoid',
  'avoidcompetitors',
  'avoidcompanies',
  'donotsourcecompanies',
  'dnscompanies',
  'excludecompanies',
  'excludedcompanies',
  'excludedcompetitors',
  'excludecompetitors',
] as const;

/**
 * The normalized labels {@link parsePastedBrief} recognises INSIDE a line (the
 * `switch` keys minus the one-letter `a` / `b`, which are too ambiguous mid-text:
 * "plan A: …"). Normalization = lowercase with spaces / `_` / `-` removed.
 */
const INLINE_BRIEF_LABELS = new Set([
  'client',
  'clientname',
  'company',
  'hiringcompany',
  'website',
  'site',
  'url',
  'industry',
  'subcategory',
  'subcategories',
  'subindustry',
  'keywords',
  'location',
  'locations',
  'geo',
  'region',
  'country',
  'size',
  'companysize',
  'headcount',
  'type',
  'companytype',
  'gradea',
  'gradeb',
  'dns',
  'donotsource',
  'exclude',
  'exclusions',
  ...COMPANY_AVOID_LABELS,
  'avoid',
  'avoiding',
  'notes',
  'note',
]);

/**
 * E12-F17-T11 — split one pasted line at every KNOWN label that follows a colon
 * mid-line ("Client: Acme. Industry: Robotics" → ["Client: Acme.", "Industry:
 * Robotics"]). Up to 4 words before each colon are tried, longest suffix first, so
 * "Acme Corp Industry:" still cuts right before "Industry". Unknown "word:" pairs
 * (e.g. `https:`) never split. A line with a label only at its start is returned
 * unchanged, so the multi-line brief behaves exactly as before.
 */
function splitAtInlineLabels(line: string): string[] {
  const cuts: number[] = [];
  for (let i = line.indexOf(':'); i > 0; i = line.indexOf(':', i + 1)) {
    const before = line.slice(0, i);
    // Try 4, 3, 2, 1 trailing words: the longest one that is a known label wins.
    for (let n = 4; n >= 1; n--) {
      const m = new RegExp(
        `(?<![A-Za-z_-])([A-Za-z][A-Za-z_-]*(?:[ \\t]+[A-Za-z][A-Za-z_-]*){${n - 1}})[ \\t]*$`,
      ).exec(before);
      if (!m) continue;
      const key = m[1]!.toLowerCase().replace(/[\s_-]+/g, '');
      if (!INLINE_BRIEF_LABELS.has(key)) continue;
      if (line.slice(0, m.index).trim()) cuts.push(m.index);
      break;
    }
  }
  if (!cuts.length) return [line];
  const out: string[] = [];
  let from = 0;
  for (const c of cuts) {
    out.push(line.slice(from, c));
    from = c;
  }
  out.push(line.slice(from));
  return out.map((s) => s.trim()).filter(Boolean);
}

/** Legal-form abbreviations whose trailing period belongs to the company name. */
const NAME_ABBREVIATIONS = new Set([
  'corp',
  'inc',
  'ltd',
  'co',
  'llc',
  'plc',
  'gmbh',
  'ag',
  'sa',
  'bv',
  'nv',
  'srl',
  'spa',
  'pty',
  'lp',
  'llp',
]);

/** Words whose period never ends a sentence ("St. Jude", "J. P. Morgan"). */
function isNonTerminalAbbreviation(word: string): boolean {
  const w = word.replace(/\.$/, '').toLowerCase();
  return /^[a-z]$/.test(w) || ['st', 'dr', 'mr', 'mrs', 'ms'].includes(w);
}

/** True when the word right before a period is a legal form ("Corp.", "Inc."). */
function endsWithNameAbbreviation(text: string): boolean {
  const m = /([A-Za-z]+)\.$/.exec(text);
  return !!m && NAME_ABBREVIATIONS.has(m[1]!.toLowerCase());
}

/**
 * Trim a "Label: value" value: surrounding blanks, the `,` / `;` left over after an
 * inline-label split, and a sentence-final period — unless it closes a legal form
 * ("Acme E2E Corp.") or an initial.
 */
function cleanBriefValue(raw: string): string {
  let v = raw.trim().replace(/[\s,;]+$/, '');
  if (/[^.]\.$/.test(v) && !endsWithNameAbbreviation(v)) {
    const lastWord = /(\S+)$/.exec(v)?.[1] ?? '';
    if (!isNonTerminalAbbreviation(lastWord)) v = v.slice(0, -1).trimEnd();
  }
  return v;
}

/**
 * E12-F17-T11 — cut a name-like value at its first sentence end: "Acme E2E Corp.
 * We are hiring …" → head "Acme E2E Corp.", tail "We are hiring …". The period of
 * a legal form stays on the name; initials / "St." never end the sentence.
 */
function splitFirstSentence(value: string): { head: string; tail: string } {
  const re = /[.!?](?=\s+\S)/g;
  for (let m = re.exec(value); m; m = re.exec(value)) {
    const before = value.slice(0, m.index + 1);
    const lastWord = /(\S+)$/.exec(before)?.[1] ?? '';
    if (m[0] === '.' && isNonTerminalAbbreviation(lastWord)) continue;
    const keepPeriod = m[0] === '.' && endsWithNameAbbreviation(before);
    const head = (keepPeriod ? before : value.slice(0, m.index)).trim();
    const tail = value.slice(m.index + 1).trim();
    if (head) return { head, tail };
  }
  return { head: value, tail: '' };
}

/**
 * Heuristic decomposition of a pasted recruiter request into intake fields
 * (the prototype `pasteParse()` is a mock; the real accelerator is the T04
 * `research_criteria_normalize` AI stage, which needs a persisted draft id).
 * This client-side pass recognises the common `Label: value` lines a recruiter
 * email/Word request uses so a fresh draft (no id yet) is still autofilled;
 * once the draft exists the AI normalize can refine it. Pure / DOM-free / tested.
 *
 * Returns `null` for empty input (the caller warns "paste a request first").
 */
export function parsePastedBrief(raw: string): ParsedField | null {
  const text = (raw ?? '').trim();
  if (!text) return null;
  const out: ParsedField = {};
  const notes: string[] = [];
  // E12-F17-T11 — a one-line brief ("Client: X. Industry: Y. We need …") carries
  // several labels on ONE line; split it at every known inline label first, so
  // each label only gets its own fragment instead of the whole remainder.
  const segments = text.split(/\r?\n/).flatMap(splitAtInlineLabels);
  for (const line of segments) {
    const m = /^\s*([A-Za-z][A-Za-z /_-]{1,40}?)\s*[:\-–]\s*(.+)$/.exec(line);
    if (!m) {
      const t = line.trim();
      if (t) notes.push(t);
      continue;
    }
    const key = m[1]!
      .trim()
      .toLowerCase()
      .replace(/[\s_-]+/g, '');
    const val = cleanBriefValue(m[2]!);
    if (!val) continue;
    switch (key) {
      case 'client':
      case 'clientname':
      case 'company':
      case 'hiringcompany': {
        // The name ends at the first sentence end; the prose after it is brief text.
        const { head, tail } = splitFirstSentence(m[2]!.trim());
        out.clientName = cleanBriefValue(head);
        if (tail) notes.push(tail);
        break;
      }
      case 'website':
      case 'site':
      case 'url':
        out.clientWebsite = val;
        break;
      case 'industry':
        out.industry = val;
        break;
      case 'subcategory':
      case 'subcategories':
      case 'subindustry':
      case 'keywords':
        out.subCategories = splitList(val);
        break;
      case 'location':
      case 'locations':
      case 'geo':
      case 'region':
      case 'country':
        out.locations = splitList(val);
        break;
      case 'size':
      case 'companysize':
      case 'headcount':
        out.sizeGlobal = snapSizeBand(val);
        break;
      case 'type':
      case 'companytype':
        out.companyTypes = splitList(val);
        break;
      case 'gradea':
      case 'a':
        out.gradeA = val;
        break;
      case 'gradeb':
      case 'b':
        out.gradeB = val;
        break;
      case 'dns':
      case 'donotsource':
      case 'exclude':
      case 'exclusions':
      case 'competitorstoavoid':
      case 'companiestoavoid':
      case 'avoidcompetitors':
      case 'avoidcompanies':
      case 'donotsourcecompanies':
      case 'dnscompanies':
      case 'excludecompanies':
      case 'excludedcompanies':
      case 'excludedcompetitors':
      case 'excludecompetitors':
        out.dnsCompanies = [...(out.dnsCompanies ?? []), ...splitList(val)];
        break;
      // E06-F01-T10 — the brief's own "Avoid:" line (business models not to source).
      case 'avoid':
      case 'avoiding':
        out.avoid = splitList(val);
        break;
      case 'notes':
      case 'note':
        notes.push(val);
        break;
      default:
        notes.push(line.trim());
    }
  }
  if (notes.length) out.freeText = notes.join('\n');
  return out;
}

/**
 * Merge a {@link parsePastedBrief} result into existing intake criteria without
 * clobbering fields the recruiter already typed: scalars only overwrite when the
 * current value is blank; collections are appended (deduped). Grade A/B land in
 * `gradeDefinitions`. Pure — the form calls `setCriteria(applyPastedBrief(...))`.
 */
export function applyPastedBrief(current: IntakeCriteria, parsed: ParsedField): IntakeCriteria {
  const fillScalar = (cur: string, next: string | undefined): string =>
    cur.trim() ? cur : (next ?? cur);
  const mergeList = (cur: readonly string[], next: string[] | undefined): string[] => {
    if (!next?.length) return [...cur];
    const seen = new Set(cur.map((c) => c.toLowerCase()));
    const add = next.filter((n) => !seen.has(n.toLowerCase()));
    return [...cur, ...add];
  };
  return {
    ...current,
    clientName: fillScalar(current.clientName, parsed.clientName),
    clientWebsite: fillScalar(current.clientWebsite, parsed.clientWebsite),
    industry: fillScalar(current.industry, parsed.industry),
    subCategories: mergeList(current.subCategories, parsed.subCategories),
    locations: mergeIntoLocations(current.locations, parsed.locations),
    sizeGlobal: fillScalar(current.sizeGlobal, parsed.sizeGlobal),
    companyTypes: mergeList(current.companyTypes, parsed.companyTypes),
    dnsCompanies: mergeList(current.dnsCompanies, parsed.dnsCompanies),
    avoid: mergeList(current.avoid, parsed.avoid),
    gradeDefinitions: {
      a: fillScalar(current.gradeDefinitions.a, parsed.gradeA),
      b: fillScalar(current.gradeDefinitions.b, parsed.gradeB),
      c: current.gradeDefinitions.c,
    },
    freeText: current.freeText.trim() ? current.freeText : (parsed.freeText ?? current.freeText),
  };
}

/** Append plain location labels to the IntakeLocation[] (no radius), deduped. */
function mergeIntoLocations(
  current: readonly IntakeLocation[],
  labels: string[] | undefined,
): IntakeLocation[] {
  if (!labels?.length) return [...current];
  const seen = new Set(current.map((l) => l.label.toLowerCase()));
  const add = labels
    .filter((l) => !seen.has(l.toLowerCase()))
    .map<IntakeLocation>((label) => ({ label, lat: null, lng: null, radiusKm: null }));
  return [...current, ...add];
}

// ---------------------------------------------------------------------------
// Location stages (E06-F01-T10) — "Europe first, then Global excl. UK"
// ---------------------------------------------------------------------------

/** The stage of an intake location (absent = 1). */
export function locationStage(location: IntakeLocation): number {
  return location.stage ?? 1;
}

/** How many stage rows the form shows: the highest stage in use (at least 1). */
export function locationStageCount(locations: readonly IntakeLocation[]): number {
  return locations.reduce((max, l) => Math.max(max, locationStage(l)), 1);
}

/** The location labels of one stage (1-based), in order. */
export function stageLocationLabels(locations: readonly IntakeLocation[], stage: number): string[] {
  return locations.filter((l) => locationStage(l) === stage).map((l) => l.label);
}

/**
 * Replace the labels of one stage, keeping every other stage as it was. A stage-1
 * location carries no `stage` key (so a one-stage request stays byte-identical to a
 * pre-T10 one); later stages carry theirs. Result is ordered by stage. Pure.
 */
export function setStageLocations(
  locations: readonly IntakeLocation[],
  stage: number,
  labels: readonly string[],
  radiusKm: number | null,
): IntakeLocation[] {
  const next: IntakeLocation[] = [
    ...locations.filter((l) => locationStage(l) !== stage),
    ...labels.map<IntakeLocation>((label) => ({
      label,
      lat: null,
      lng: null,
      radiusKm,
      ...(stage > 1 ? { stage } : {}),
    })),
  ];
  return next
    .map((l, i) => ({ l, i }))
    .sort((a, b) => locationStage(a.l) - locationStage(b.l) || a.i - b.i)
    .map((x) => x.l);
}

/**
 * Drop one stage (its locations go) and move every later stage one step up, so
 * the stages stay 1..N without a gap. Pure.
 */
export function removeLocationStage(
  locations: readonly IntakeLocation[],
  stage: number,
): IntakeLocation[] {
  return locations
    .filter((l) => locationStage(l) !== stage)
    .map((l) => {
      const s = locationStage(l);
      if (s < stage) return l;
      const moved: IntakeLocation = { ...l };
      delete moved.stage;
      if (s - 1 > 1) moved.stage = s - 1;
      return moved;
    });
}

/** Human caption of a stage row ("Stage 1 — searched first", "Stage 2 — then, if …"). */
export function locationStageCaption(stage: number): string {
  return stage === 1
    ? 'Stage 1 — searched first'
    : `Stage ${stage} — only if the earlier stages did not reach the target count`;
}

// ---------------------------------------------------------------------------
// AI-paste accelerator apply (E06-F01-T04) — CriteriaNormalizeResult → criteria
// ---------------------------------------------------------------------------

/**
 * Merge the AI `research_criteria_normalize` result onto existing intake criteria
 * (same non-clobbering spirit as {@link applyPastedBrief}): HARD filters fill only
 * blank scalars, list criteria are appended (deduped), grade definitions fill only
 * blanks, and the AI-owned decomposition outputs (softCriteria, gradeScope,
 * verifyStrictness, targetCount) are applied as the authoritative values. Resolved
 * geo tiles (or the plain HARD geo labels, carrying `radiusKm`) are merged into the
 * existing locations by label. Pure — the form calls
 * `setCriteria(applyNormalizeResult(...))`.
 *
 * E06-F01-T09: the PLAIN intake fields (industry, subCategories, client block,
 * sizeGlobal, companyTypes, fundingStages, purpose) are merged too. They used to
 * be skipped here ("not part of the normalize output") which — together with the
 * missing contract slots — is why "Parse & autofill" filled the location and
 * nothing else. Fill rules stay non-clobbering: a scalar the recruiter already
 * typed wins, lists are appended deduped, `sizeGlobal` is snapped onto a known
 * band ({@link snapSizeBand}) so the SELECT can actually show it, and `purpose`
 * only moves when the AI states one AND the toggle still sits on its default.
 *
 * DEFENSIVE READS: the SPA does not zod-parse the response body (intakeApi casts
 * it), so an older API build — or a test fixture written before T09 — delivers a
 * result object with these keys ABSENT. Every read below therefore goes through
 * `text()` / `list()`, which accept `undefined`.
 */
export function applyNormalizeResult(
  current: IntakeCriteria,
  result: CriteriaNormalizeResult,
): IntakeCriteria {
  const text = (value: string | undefined): string => (value ?? '').trim();
  const list = (value: readonly string[] | undefined): readonly string[] => value ?? [];
  const mergeList = (cur: readonly string[], next: readonly string[] | undefined): string[] => {
    const seen = new Set(cur.map((c) => c.trim().toLowerCase()));
    const add = list(next)
      .map((n) => n.trim())
      .filter((n) => n !== '' && !seen.has(n.toLowerCase()));
    return [...cur, ...add];
  };
  const fillNum = (cur: number | null, next: number | null): number | null =>
    cur != null ? cur : next;
  const fillGrade = (cur: string, next: string): string => (cur.trim() ? cur : next);
  const dnsCompanies = mergeList(current.dnsCompanies, result.dnsCompanies);
  const dnsKeys = new Set(dnsCompanies.map((d) => d.trim().toLowerCase()));
  /** Scalar fill: the recruiter's typed value always wins over the AI's. */
  const fillText = (cur: string, next: string | undefined): string =>
    cur.trim() ? cur : text(next);

  // Locations: prefer the resolved geo tiles (they carry lat/lng/radiusKm); fall
  // back to the plain HARD geo labels with the shared radius. Merge by label.
  const geoRadiusKm = result.hardFilters.geo.radiusKm;
  const aiTiles: IntakeLocation[] =
    result.geoTiles.length > 0
      ? result.geoTiles
      : result.hardFilters.geo.locations.map((label) => ({
          label,
          lat: null,
          lng: null,
          radiusKm: geoRadiusKm ?? null,
        }));
  // E06-F01-T12: an AI tile that QUALIFIES a bare label already in the form
  // ("Georgia" → "Georgia, USA" — the bare one is the country) replaces it in place.
  // Only the "<place>, USA|Canada" shape the state lists produce qualifies.
  const refined = new Map<string, IntakeLocation>();
  for (const t of aiTiles) {
    const m = /^([^,]+),\s*(?:usa?|united states|canada)\s*$/i.exec(t.label.trim());
    if (m) refined.set(m[1]!.trim().toLowerCase(), t);
  }
  const currentLocations = current.locations.map((l) => {
    const key = l.label.trim().toLowerCase();
    const better = !l.label.includes(',') ? refined.get(key) : undefined;
    return better ? { ...better, ...(l.stage ? { stage: l.stage } : {}) } : l;
  });
  const seenLoc = new Set(currentLocations.map((l) => l.label.trim().toLowerCase()));
  const locations: IntakeLocation[] = [
    ...currentLocations,
    ...aiTiles.filter((t) => t.label.trim() !== '' && !seenLoc.has(t.label.trim().toLowerCase())),
  ];

  return {
    ...current,
    locations,
    // E06-F01-T12: Do-Not-Source States etc. → Excluding locations (appended, deduped).
    locationExclusions: mergeList(current.locationExclusions ?? [], result.locationExclusions),
    foundedFrom: fillNum(current.foundedFrom, result.hardFilters.foundedFrom),
    foundedTo: fillNum(current.foundedTo, result.hardFilters.foundedTo),
    sizeLocalCap: fillNum(current.sizeLocalCap, result.hardFilters.sizeLocalCap),
    // E12-F17-T12: an AI competitor the form already lists as Do-Not-Source is
    // never added as a Grade-A seed (the recruiter's own competitors stay as typed).
    competitors: mergeList(
      current.competitors,
      list(result.competitors).filter((c) => !dnsKeys.has(c.trim().toLowerCase())),
    ),
    dnsCompanies,
    dnsIndustries: mergeList(current.dnsIndustries, result.dnsIndustries),
    // --- T09: the plain intake fields (were never applied before) ---
    clientName: fillText(current.clientName, result.clientName),
    clientWebsite: fillText(current.clientWebsite, result.clientWebsite),
    clientAbout: fillText(current.clientAbout, result.clientAbout),
    industry: fillText(current.industry, result.industry),
    subCategories: mergeList(current.subCategories, result.subCategories),
    sizeGlobal: current.sizeGlobal.trim()
      ? current.sizeGlobal
      : snapSizeBand(text(result.sizeGlobal)),
    companyTypes: mergeList(current.companyTypes, result.companyTypes),
    fundingStages: mergeList(current.fundingStages, result.fundingStages),
    // `purpose` has no "unset" state (emptyIntakeCriteria seeds 'sourcing'), so the
    // "typed wins" test above cannot be applied to it. Rule instead: adopt the AI's
    // purpose only while the toggle still sits on the default — a deliberate
    // Market-Mapping choice is never silently downgraded back to Sourcing by a paste.
    purpose: result.purpose && current.purpose === 'sourcing' ? result.purpose : current.purpose,
    gradeScope: result.gradeScope,
    gradeDefinitions: {
      a: fillGrade(current.gradeDefinitions.a, result.gradeDefinitions.a),
      b: fillGrade(current.gradeDefinitions.b, result.gradeDefinitions.b),
      c: fillGrade(current.gradeDefinitions.c, result.gradeDefinitions.c),
    },
    verifyStrictness: result.verifyStrictness,
    targetCount: result.targetCount,
    softCriteria: result.softCriteria.length > 0 ? result.softCriteria : current.softCriteria,
  };
}

// ---------------------------------------------------------------------------
// Clone-prefill mapper (E06-F04-T05) — ResearchCriteria (legacy blob) →
// IntakeCriteria (machine-valued form) so a History clone opens Intake filled.
// ---------------------------------------------------------------------------

/**
 * The two research vocabularies are DELIBERATELY distinct (see research-intake.ts
 * header): the History clone carries the legacy-blob {@link ResearchCriteria}
 * (`client.name`, `companyType`, `funding`, display-cased `purpose`, string
 * `locations[]`, flat `gradeA/B/C`), whereas {@link IntakeForm} consumes the
 * machine-valued {@link IntakeCriteria} (`clientName`, `companyTypes`,
 * `fundingStages`, machine `purpose`, `IntakeLocation[]`, `gradeDefinitions`).
 * This mapper bridges the dictionaries so a cloned draft opens prefilled.
 *
 * Rules:
 *   - unknown / missing fields fall back to the {@link emptyIntakeCriteria}
 *     default (a partial clone still round-trips);
 *   - `purpose` maps display → machine (`'Sourcing'` → `'sourcing'`,
 *     `'Market Mapping'` → `'market_mapping'`);
 *   - flat `locations: string[]` → `IntakeLocation[]` carrying the shared
 *     `radius` as each tile's `radiusKm` (the intake is always km now);
 *   - `sizeGlobal` is snapped onto a known band ({@link snapSizeBand});
 *   - `targetCount` is clamped to the slider bounds;
 *   - `linkedProject` (a project id) is NOT part of IntakeCriteria — the caller
 *     reads it separately for the Link-to-project select (see cloneLinkedProjectId).
 */
export function researchCriteriaToIntake(criteria: ResearchCriteria | undefined): IntakeCriteria {
  const base = emptyIntakeCriteria();
  if (!criteria) return base;

  const purpose: IntakeResearchPurpose =
    criteria.purpose === 'Market Mapping' ? 'market_mapping' : 'sourcing';

  const radiusKm = typeof criteria.radius === 'number' ? criteria.radius : null;
  const locations: IntakeLocation[] = (criteria.locations ?? []).map((label) => ({
    label,
    lat: null,
    lng: null,
    radiusKm,
  }));

  const targetCount = clampTargetCount(criteria.targetCount);

  return {
    ...base,
    clientName: criteria.client?.name ?? base.clientName,
    clientWebsite: criteria.client?.website ?? base.clientWebsite,
    clientAbout: criteria.client?.about ?? base.clientAbout,
    addClientToDns: criteria.client?.addToDns ?? base.addClientToDns,
    industry: criteria.industry ?? base.industry,
    subCategories: criteria.subCategories ? [...criteria.subCategories] : base.subCategories,
    locations,
    foundedFrom: criteria.foundedFrom ?? base.foundedFrom,
    foundedTo: criteria.foundedTo ?? base.foundedTo,
    sizeGlobal: snapSizeBand(criteria.sizeGlobal),
    sizeLocalCap: criteria.sizeLocalCap ?? base.sizeLocalCap,
    companyTypes: criteria.companyType ? [...criteria.companyType] : base.companyTypes,
    fundingStages: criteria.funding ? [...criteria.funding] : base.fundingStages,
    competitors: criteria.competitors ? [...criteria.competitors] : base.competitors,
    dnsCompanies: criteria.dnsCompanies ? [...criteria.dnsCompanies] : base.dnsCompanies,
    dnsIndustries: criteria.dnsIndustries ? [...criteria.dnsIndustries] : base.dnsIndustries,
    purpose,
    gradeScope: criteria.gradeScope ?? base.gradeScope,
    gradeDefinitions: {
      a: criteria.gradeA ?? '',
      b: criteria.gradeB ?? '',
      c: criteria.gradeC ?? '',
    },
    verifyStrictness: criteria.verifyStrictness ?? base.verifyStrictness,
    freeText: criteria.freeText ?? base.freeText,
    targetCount,
  };
}

/** The linked project id carried on a cloned blob (for the Link-to-project select). */
export function cloneLinkedProjectId(criteria: ResearchCriteria | undefined): number | null {
  return typeof criteria?.linkedProject === 'number' ? criteria.linkedProject : null;
}

/** Clamp a possibly-out-of-range target count to the slider bounds. */
function clampTargetCount(value: number | undefined): number {
  if (typeof value !== 'number' || Number.isNaN(value)) return TARGET_COUNT_DEFAULT;
  return Math.min(TARGET_COUNT_MAX, Math.max(TARGET_COUNT_MIN, Math.round(value)));
}

// ---------------------------------------------------------------------------
// E06-F06-T04 — «Похожие списки» hint
// ---------------------------------------------------------------------------

/** Debounce of the similar-lists recalculation after a Targeting change (spec ~0,5 с). */
export const SIMILAR_LISTS_DEBOUNCE_MS = 500;

/**
 * The hint is recalculated only off Industry / Sub-categories / Locations — while
 * all three are empty the panel is not shown (and nothing is requested).
 */
export function hasSimilarListsTrigger(criteria: IntakeCriteria): boolean {
  return (
    criteria.industry.trim() !== '' ||
    criteria.subCategories.some((s) => s.trim() !== '') ||
    criteria.locations.some((l) => l.label.trim() !== '')
  );
}

/**
 * The POST /research/list/similar body for the form's draft: the five scored fields
 * (+ the request being edited, so it is never suggested to itself).
 */
export function similarListsInput(
  criteria: IntakeCriteria,
  excludeRequestId: number | null | undefined,
): SimilarListsRequest {
  return {
    industry: criteria.industry.trim(),
    subCategories: [...criteria.subCategories],
    locations: criteria.locations.map((l) => ({ label: l.label })),
    competitors: [...criteria.competitors],
    companyTypes: [...criteria.companyTypes],
    excludeRequestId:
      typeof excludeRequestId === 'number' && excludeRequestId > 0 ? excludeRequestId : null,
  };
}

/** Fields that are bookkeeping, not something the recruiter typed. */
const BLANK_CHECK_IGNORED: ReadonlySet<keyof IntakeCriteria> = new Set<keyof IntakeCriteria>([
  'clientId',
  'metroUnit',
]);

/**
 * Whether the form holds nothing the recruiter entered (every field still at its
 * {@link emptyIntakeCriteria} default). «Взять критерии» overwrites a NON-blank form
 * only after a confirmation.
 */
export function isIntakeCriteriaBlank(criteria: IntakeCriteria): boolean {
  const empty = emptyIntakeCriteria();
  return (Object.keys(empty) as Array<keyof IntakeCriteria>).every(
    (key) =>
      BLANK_CHECK_IGNORED.has(key) || JSON.stringify(criteria[key]) === JSON.stringify(empty[key]),
  );
}

// Re-export the canonical enum tuples for the toggles.
export { INTAKE_RESEARCH_PURPOSES, GRADE_SCOPES, VERIFY_STRICTNESS };
